import React, { useEffect, useState } from "react";
import Link from "next/link";
import Head from "next/head";
import { useRouter } from "next/router";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import BlogImage from "components/customImages/BlogImage";
import { getCurrentPath, getOrigin } from "utils/common";
import Button from "components/ui_components/button";
import { BASE_URL } from "helpers/constants/routes";

function Index() {
  const router = useRouter();
  const { locale } = router;

  useEffect(() => {
    document.documentElement.dir = "ltr";
  }, [locale]);

  const currentPath = getCurrentPath();
  const origin = getOrigin();
  return (
    <>
      <Head>
        <title>{"Comment Choisir la Destination Parfaite pour vos Vacances d'Été | Yawakit"}</title>
        <meta
          name="description"
          content="Découvrez nos conseils essentiels pour choisir les meilleures vacances d'été. Des destinations de rêve aux astuces pratiques, préparez-vous avec notre guide !"
        />
        <link rel="alternate" hrefLang="fr" href={`${origin}${currentPath}`} />
        <link rel="alternate" hrefLang="ar" href={`${origin}/ar${currentPath}`} />
      </Head>

      <div className="flex flex-col w-full py-10">
        <div className="max-w-[680px] m-auto px-6">
          <h1 className="font-bold text-md lg:text-lg  mb-10 text-center ">{`Vacances d'été : les clés pour faire le bon choix !`}</h1>
          <time dateTime="02-07-2023">Juillet 2, 2023</time>
          <p className="text-justify">
            {`L'été approche à grands pas, et c'est le moment idéal pour penser aux
          vacances ! Il est important de profiter pleinement de cette période
          tant attendue. C'est pourquoi nous vous dévoilons les meilleures
          recommandations pour des vacances inoubliables. Que vous rêviez de
          plages paradisiaques, d'aventures en pleine nature ou d'activités de
          loisir pour vos enfants, nous avons sélectionné des options adaptées à
          tous les goûts et à tous les budgets. Préparez-vous à vivre des
          moments exceptionnels cet été. Suivez nos conseils et faites de vos
          vacances une expérience mémorable !`}
          </p>
          <h2 className="font-bold text-md lg:text-lg m-5">{`Voyage et destinations : Préparez vos vacances d'été`}</h2>
          <p className="text-justify">
            {`Où partirons-nous cet été : en Tunisie ou à l'étranger ? La réussite
          des vacances commence par une organisation sans faille. C'est pourquoi
          il faut bien choisir son agence de voyage afin que son séjour se passe
          comme on l'imagine : au mieux ! Voici quelques conseils pour vous
          aider à faire le bon choix :`}
          </p>

          <ol className="list-decimal pl-3 text-justify">
            <li className="p-5 font-bold">{`Définir vos besoins et préférences :`}</li>
            <ul className="list-disc pl-6 text-justify">
              <li>
                {`Type de Voyage : Déterminez si vous avez besoin d'un voyage
              organisé, d'une lune de miel, d'un voyage en groupe, ou d'une
              escapade solo.`}
              </li>
              <li>
                {`Destination : Ayez une idée précise de la destination que vous
              souhaitez visiter et renseignez-vous sur les agences spécialisées
              dans cette région.`}
              </li>
            </ul>
            <li className="p-5">
              <span className="font-bold">{`Avis et témoignages:`} </span>
              {`Consultez
            les avis en ligne sur des plateformes comme TripAdvisor, Facebook,
            Google Reviews.`}
            </li>
            <li className="p-5">
              <span className="font-bold">{`Forfaits et offres:`} </span>{" "}
              {`Comparez les
            forfaits proposés par différentes agences pour voir ce qui est
            inclus (hébergement, repas, visites guidées, etc.) et à quel prix.`}
            </li>
            <li className="p-5">
              <span className="font-bold">{`Assistance:`}</span>{" "}
              {`Assurez-vous que
            l'agence offre une assistance 24/7 en cas de problème pendant votre
            voyage.`}
            </li>
            <li className="p-5">
              <span className="font-bold">{`Politique d'Annulation:`}</span>{" "}
              {`Vérifiez
            les conditions d'annulation et les politiques de remboursement avant
            de réserver.`}
            </li>
            <li className="p-5">
              <span className="font-bold">{`Service Client:`}</span>{" "}
              {`Testez le
            service client en posant des questions avant de réserver. La
            réactivité et la courtoisie des agents peuvent être un bon
            indicateur de la qualité du service.`}
            </li>
          </ol>
          <p className="text-justify">
            {`Prendre le temps de choisir`}
            <span className="font-bold">{`la bonne agence de voyage`}</span>{" "}
            {`peut
          faire toute la différence entre un voyage stressant et une expérience
          inoubliable.`}
          </p>
          <div className="flex justify-center p-5">
            <BlogImage
              imageName="beautiful-girl-standing-boat-looking-mountains-ratchaprapha-dam-khao-sok-national-park-surat-thani-province-thailand.jpg"
              alt="Voyage et destinations"
              className={"object-contain"}
              imageUrl="beautiful-girl-standing-boat-looking-mountains-ratchaprapha-dam-khao-sok-national-park-surat-thani-province-thailand.jpg"
            />
          </div>
          <p className="text-justify">
            {`Pour cet été, les possibilités de destinations sont infinies, on vous
          aide à faire le`}{" "}
            <span className="font-bold">{`meilleur choix `}</span>
            {`selon vos préférences. Si vous rêvez de plages de sable fin et d'eaux
          turquoise, pensez aux joyaux méditerranéens tels que la Grèce avec ses
          îles ensoleillées, l'Espagne avec ses côtes animées, ou encore la
          Turquie avec ses stations balnéaires accueillantes. Pour ceux qui
          recherchent des destinations un peu plus exotiques, les plages de la
          Thaïlande, des Maldives ou des Seychelles promettent des vacances
          paradisiaques.`}
          </p>
          <div className="flex justify-center m-8 	">
            <Button>
              <Link
                target="_blank"
                rel="noopener"
                href={`${BASE_URL}categories/loisir?extraOptions=%7B%22sub_category%22%3A%7B%22name%22%3A%7B%22containsi%22%3A%22Loi+%3E+agence+de+voyages%22%7D%7D%7D`}
                passHref
                legacyBehavior
              >
                {`Faites le bon choix ici`}
              </Link>
            </Button>
          </div>
          <h2 className="font-bold text-md lg:text-lg m-5">{`Vacances d'été en harmonie avec la nature`}</h2>
          <p className="text-justify">
            {`Pour les amoureux de la nature et de l'aventure, on vous propose des
          espaces camping en plein air pour des vacances en parfaite harmonie
          avec les merveilles naturelles de la Tunisie. Que vous soyez un
          campeur aguerri, habitué à dresser votre tente au cœur de la nature
          sauvage, ou que vous aspirez à découvrir cette expérience pour la
          première fois, nos recommandations offrent une diversité d'options
          pour répondre à tous les profils de voyageurs.`}
            <span className="font-bold">{`Le camping en plein air est un choix idéal`}</span>
            {`pour passer`} <span className="font-bold">{`les vacances d'été`}</span>
            {`,
          permettant de se ressourcer loin du tumulte de la vie quotidienne et
          de se connecter profondément avec la nature. Imaginez-vous sous un
          ciel étoilé, le son apaisant des vagues en toile de fond ou le calme
          réconfortant d'une forêt luxuriante: voilà l'expérience unique que
          vous offre le camping avec Yawakit.`}
          </p>
          <div className="flex justify-center m-8">
            <Button>
              <Link
                target="_blank"
                rel="noopener"
                href={`${BASE_URL}categories/loisir?extraOptions=%7B%22sub_category%22%3A%7B%22name%22%3A%7B%22containsi%22%3A%22Loi+%3E+agence+de+voyages%22%7D%7D%7D`}
                passHref
                legacyBehavior
              >
                {`Voir plus d&apos;espace de loisir`}
              </Link>
            </Button>
          </div>
          <h2 className="font-bold text-md lg:text-lg m-5">
            {`Les Maisons d'Hôtes : une expérience authentique pour vos vacances
          d'été`}
          </h2>
          <p className="text-justify">
            {`Pour ceux qui souhaitent passer leurs vacances en Tunisie, on vous
          propose une sélection exceptionnelle de`}
            <span className="font-bold">{`maisons d'hôtes`}</span>{" "}
            {`qui sauront rendre
          votre`}{" "}
            <span className="font-bold">{`séjour inoubliable`}</span>.{" "}
            {`Nos
          recommandations incluent des maisons d'hôtes situées dans des régions
          variées, allant des charmants villages de l'intérieur du pays aux
          côtés pittoresques de la Méditerranée. Ces établissements, souvent
          gérés par des familles locales, offrent un accueil chaleureux et une
          ambiance conviviale, permettant de découvrir la culture tunisienne de
          manière authentique. Vous pourrez profiter de décors uniques, inspirés
          des traditions et de l'architecture tunisiennes, tout en bénéficiant
          de tout le confort moderne. Pour les Tunisiens résidant
          à l'étranger qui reviennent en vacances en Tunisie, les maisons
          d'hôtes représentent une option idéale. Elles offrent une`}
            <span className="font-bold">{`expérience authentique et personnalisée.`}</span>
            <br /> <br />{" "}
            {`En optant pour une maison d'hôtes recommandée par
          Yawakit, vous choisissez une expérience unique et différente.`}
          </p>
          <div className="flex justify-center p-5">
            <BlogImage
              imageName="umbrella-pool-chair.jpg"
              alt="Les Maisons d'Hôtes en Tunise"
              className="object-contain"
              imageUrl="umbrella-pool-chair.jpg"
            />
          </div>
          <h2 className="font-bold text-md lg:text-lg m-5">{`Activités de loisir pour enfants pour des vacances inoubliables`}</h2>
          <p className="text-justify">
            {`Qui dit vacances d'été dit enfants ! Dans cet article nous pensons au
          bien-être de vos enfants en vous proposant une sélection d'activités
          divertissantes et adaptées à leur âge. En Tunisie, une variété
          d'options s'offre à vous pour rendre leurs vacances d'été mémorables.`}
          </p>
          <ul className="list-disc pl-6 text-justify">
            <li className="pt-5">
              {`Les parcs de loisirs sont des destinations populaires où les enfants
            peuvent s'amuser et se dépenser. Avec leurs manèges, attractions et
            jeux, ces parcs offrent une journée de divertissement garantie pour
            toute la famille.`}
            </li>
            <li>
              {`Les aires de jeux en plein air sont également très appréciées,
            offrant une multitude d'équipements ludiques tels que des toboggans,
            des balançoires et des structures d'escalade. Ces espaces permettent
            aux enfants de se défouler et de socialiser tout en profitant du
            grand air et de la nature.`}
            </li>
          </ul>
          <div className="flex justify-center p-5">
            <BlogImage
              imageName="kids-drawing-playing.jpg"
              alt="Activités de loisir pour enfants"
              className="object-contain"
              imageUrl="kids-drawing-playing.jpg"
            />
          </div>
          <p className="text-justify">
            {`Ne laissez pas passer l'opportunité de vivre des vacances inoubliables
          ! Que vous rêviez de découvrir de nouvelles destinations, de camper en
          plein air, de séjourner dans des maisons d'hôtes authentiques, ou de
          divertir vos enfants avec des activités passionnantes. Yawakit a tout
          ce qu'il vous faut. Consultez notre site pour explorer toutes nos
          recommandations et trouver les meilleures options pour vos vacances
          d'été.`}
          </p>
          <div className="flex justify-center m-8">
            <Button>
              <Link
                target="_blank"
                rel="noopener"
                href={`${BASE_URL}categories/loisir?extraOptions=%7B%22sub_category%22%3A%7B%22name%22%3A%7B%22containsi%22%3A%22Loi+%3E+parc+d%27enfants%22%7D%7D%7D`}
                passHref
                legacyBehavior
              >
                {`Découvrez nos espaces de loisir`}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["layout", "categories", "signup_promotion"])),
    },
  };
}

export default Index;
